<template>
  <div>
    <VacancyCandidatesKanbanCreate
      @created="candidateCreated"
      v-model="createCandidateDialog"
      :vacancy="vacancy"
    />

    <VacancyCandidatesKanbanOfficeInterview
      v-model="recordOfficeInterviewDialog"
      :vacancy="vacancy"
    />

    <VacancyCandidatesKanbanVideoCall
      v-model="createVideoCallDialog"
      :vacancy="vacancy"
    />

    <VacancyCandidatesDialog
      v-if="vacanciDialogIsVisible(inofficeInterviewDialog, notificationType.CANDIDATE_INOFFICE_INTERVIEW)"
      v-model="inofficeInterviewDialog"
      :title="$t('dialogHeader')"
    >
      <template #text>
        {{ activeCandidate.lastName }} {{ activeCandidate.firstName }}
        <p>{{ activeCandidate.phone }}</p>
      </template>
      <CandidateContactForm
        :candidate="activeCandidate"
        :email-text="compileNotificationTemplate(
          notificationType.CANDIDATE_INOFFICE_INTERVIEW,
          notificationChannelType.EMAIL
        )"
        :sms-text="compileNotificationTemplate(
          notificationType.CANDIDATE_INOFFICE_INTERVIEW,
          notificationChannelType.SMS
        )"
        @cancel="inofficeInterviewDialog = false"
      />
    </VacancyCandidatesDialog>

    <VacancyCandidatesDialog
      v-if="vacanciDialogIsVisible(jobOfferDialog, notificationType.CANDIDATE_JOB_OFFER)"
      v-model="jobOfferDialog"
      :title="$t('dialogHeader')"
    >
      <template #text>
        {{ activeCandidate.lastName }} {{ activeCandidate.firstName }}
        <p>{{ activeCandidate.phone }}</p>
      </template>
      <CandidateContactForm
        :candidate="activeCandidate"
        :email-text="compileNotificationTemplate(
          notificationType.CANDIDATE_JOB_OFFER,
          notificationChannelType.EMAIL
        )"
        :sms-text="compileNotificationTemplate(
          notificationType.CANDIDATE_JOB_OFFER,
          notificationChannelType.SMS
        )"
        @cancel="jobOfferDialog = false"
      />
    </VacancyCandidatesDialog>

    <VacancyCandidatesDialog
      v-if="vacanciDialogIsVisible(rejectNotificationDialog, notificationType.CANDIDATE_DECLINED)"
      v-model="rejectNotificationDialog"
      :title="$t('dialogHeader')"
    >
      <template #text>
        <div class="subtitle-1">
          {{ activeCandidate.lastName }} {{ activeCandidate.firstName }}
        </div>
        <p>{{ activeCandidate.email }}</p>
      </template>
      <CandidateRejectNotificationForm
        :candidate="activeCandidate"
        :email-text="compileNotificationTemplate(
          notificationType.CANDIDATE_DECLINED,
          notificationChannelType.EMAIL
        )"
        @cancel="rejectNotificationDialog = false"
      />
    </VacancyCandidatesDialog>
  </div>
</template>

<script>
import VacancyCandidatesKanbanCreate from '@/components/Vacancy/VacancyCandidatesKanbanCreate';
import VacancyCandidatesKanbanOfficeInterview from '@/components/Vacancy/VacancyCandidatesKanbanOfficeInterview';
import CandidateContactForm from '@/forms/CandidateContactForm';
import VacancyCandidatesDialog from '@/components/Vacancy/VacancyCandidatesDialog';
import CandidateRejectNotificationForm from '@/forms/CandidateRejectNotificationForm';
import useKanbanDialogs from '@/compositions/useKanbanDialogs';
import { notificationChannelType, notificationType } from '@/enums';
import { compileNotificationTemplate } from '@/functions';
import useCandidates from '@/compositions/useCandidates';
import { toRefs } from '@vue/composition-api';
import NotificationTemplate from '@/orm/models/Company/NotificationTemplate/NotificationTemplate';
import useUser from '@/compositions/useUser';
import useNotificationTemplateSettings from '@/compositions/useNotificationTemplateSettings';
import VacancyCandidatesKanbanVideoCall
  from '@/components/Vacancy/VacancyCandidatesKanbanVideoCall';

export default {
  name: 'VacancyCandidatesDialogs',
  components: {
    VacancyCandidatesKanbanVideoCall,
    CandidateRejectNotificationForm,
    VacancyCandidatesDialog,
    CandidateContactForm,
    VacancyCandidatesKanbanOfficeInterview,
    VacancyCandidatesKanbanCreate,
  },
  props: {
    activeCandidate: {
      type: Object,
    },
    vacancy: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { activeCandidate, vacancy } = toRefs(props);
    const { inviteCandidate } = useCandidates();
    const { currentUser } = useUser();
    const { notificationTemplatesSettings } = useNotificationTemplateSettings();

    function isNotificationEnabled(type) {
      return notificationTemplatesSettings.value.some(
        (template) => template.notificationType === type && template.enabled,
      );
    }

    function vacanciDialogIsVisible(dialog, notification) {
      return activeCandidate.value && dialog && isNotificationEnabled(notification);
    }

    return {
      ...useKanbanDialogs(true),
      notificationType,
      notificationChannelType,
      compileNotificationTemplate(type, channel) {
        const template = NotificationTemplate.query()
          .where('companyId', currentUser.value.company?.id)
          .where('type', type)
          .where('channel', channel)
          .first();

        return compileNotificationTemplate(
          template?.template ? template.template : '',
          currentUser.value,
          currentUser.value.company,
          vacancy.value,
          activeCandidate.value,
        );
      },
      inviteCandidate,
      isNotificationEnabled,
      vacanciDialogIsVisible,
      candidateCreated(candidate) {
        if (!activeCandidate.value) {
          activeCandidate.value = candidate;
        }
      },
    };
  },
};
</script>

<i18n locale="en" lang="yaml">
dialogHeader: Do you want to send a message?
</i18n>

<i18n locale="ru" lang="yaml">
dialogHeader: Хотите отправить сообщение кандидату?
</i18n>
